import React, { useEffect, useState } from 'react'
import moment from 'moment'
import {
  Grid,
  Box,
  Button,
  Typography,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  makeStyles,
  Chip,
  Avatar,
  Divider
} from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import { Formik, Form } from 'formik'
import CustomTextField from 'components/Form/CustomTextField'
import CustomBox from 'components/Form/CustomBox'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import ProgressButton from 'components/ProgressButton'
import { adminsFormSchema, updateUserSchema } from 'schemas/admins'
import { createUser, fetchUserByUserId, updateUserPasswordByUserId } from 'store/Admins'
import { useDispatch } from 'react-redux'
import { removeLeadingAndTrailingSpaces } from 'utils'
import conferenceSlice, { fetchConference, fetchConferenceById } from 'store/conference'
import ViewHeader from 'components/ViewHeader'
import ActionBar from 'components/ActionBar'
import DuplicateAssignmentDialog from 'components/DuplicateAssignmentDialog'
import ConfirmModal from 'components/ConfirmationModal'
import { toast } from 'utils/notify'
import { Roles } from 'constants/index'

const AdminsForm = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const location = useLocation()
  const confId = new URLSearchParams(location.search).get('conferenceId')
  const history = useHistory()
  const { adminId } = useParams()
  const [loading, setLoading] = useState(false)
  const [pendingPayload, setPendingPayload] = useState(null)
  const [duplicateConferences, setDuplicateConferences] = useState([])
  const [pendingPasswordUpdate, setPendingPasswordUpdate] = useState(null)
  const {
    conferences,
    conference,
    inProgress: confProgress
  } = useSelector((state) => state.conference)
  const { admin, inProgress, isCreatedOrUpdated } = useSelector((state) => state.admins)
  const { user } = useSelector((state) => state.auth)

  const initialValues = {
    email: '',
    fullName: '',
    password: '',
    role: 'admin'
  }

  const getConference = (confId) => conferences?.find((c) => c?.id === confId)

  const getConferenceName = (confId) => getConference(confId)?.confName

  const isConferenceExpired = (confId) => {
    const conf = getConference(confId)
    return Boolean(
      conf?.isArchived &&
        conf?.viewableUntil &&
        moment(conf.viewableUntil).isBefore(moment(), 'day')
    )
  }

  const getAvailableRoles = () => {
    // For now, return both roles as this menu is only for super admins
    return [
      { value: 'admin', label: 'Admin' },
      { value: 'owner', label: 'Owner' }
    ]
  }

  useEffect(() => {
    if (confId) {
      dispatch(fetchConferenceById(confId))
    }
    return () => {
      dispatch(conferenceSlice.actions.setConference({}))
    }
  }, [confId])

  useEffect(() => {
    if (adminId) {
      dispatch(fetchUserByUserId(adminId))
      dispatch(fetchConference())
    }
  }, [dispatch, adminId])

  useEffect(() => {
    if (isCreatedOrUpdated) {
      history.goBack()
    }
  }, [isCreatedOrUpdated])

  const submitHandler = async (values, { force = false } = {}) => {
    setLoading(true)
    const payload = {
      fullName: removeLeadingAndTrailingSpaces(values.fullName),
      email: removeLeadingAndTrailingSpaces(values.email).toLowerCase(),
      password: removeLeadingAndTrailingSpaces(values.password),
      confId: confId,
      role: values.role,
      callerUid: user?.id,
      ...(force && { force: true })
    }
    try {
      const result = await createUser(payload)
      if (result?.alreadyAssigned) {
        const isInCurrentConf = result.conferences?.some((c) => c.id === confId)
        if (isInCurrentConf) {
          toast.warning(
            'This user is already assigned to this conference. Please use a different email address.'
          )
          setLoading(false)
          return
        }
        setPendingPayload(values)
        setDuplicateConferences(result.conferences)
        setLoading(false)
        return
      }
      history.goBack()
    } catch (error) {
      toast.error(
        error?.response?.data?.error?.message ||
          error?.response?.data?.msg ||
          'Something went wrong.'
      )
    }
    setLoading(false)
  }

  const handleConfirmDuplicate = async () => {
    if (!pendingPayload) return
    await submitHandler(pendingPayload, { force: true })
    setPendingPayload(null)
    setDuplicateConferences([])
  }

  const handleCancelDuplicate = () => {
    setPendingPayload(null)
    setDuplicateConferences([])
  }

  const submitAdminUpdate = async (values) => {
    setLoading(true)
    const requestData = {
      uid: adminId,
      confId: confId,
      fullName: removeLeadingAndTrailingSpaces(values.fullName),
      ...(values.newPassword && { newPassword: values.newPassword })
    }

    // Add role if user is super admin or organizer and role is provided
    if ((user?.role === Roles.SUPER_ADMIN || user?.role === Roles.ORGANIZER) && values.role) {
      requestData.role = values.role
    }

    await dispatch(updateUserPasswordByUserId(requestData))
    setLoading(false)
    history.goBack()
  }

  const handleConfirmPasswordUpdate = async () => {
    if (!pendingPasswordUpdate) return
    const values = pendingPasswordUpdate
    setPendingPasswordUpdate(null)
    await submitAdminUpdate(values)
  }

  if (confId && confProgress)
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: '1.5rem' }}>
        <CircularProgress />
      </Box>
    )

  return (
    <CustomBox>
      <ViewHeader title={`${adminId ? 'Edit' : 'Create'} Conference User`}>
        <ActionBar actionText="Back" onAction={() => history.goBack()} />
      </ViewHeader>
      {confId && (
        <Box className={classes.confBanner} style={{ marginBottom: '1.5rem' }}>
          <Typography className={classes.confBannerLabel}>Conference</Typography>
          <Typography className={classes.confBannerName}>{conference?.confName}</Typography>
        </Box>
      )}

      {adminId && inProgress ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: '1.5rem' }}>
          <CircularProgress />
        </Box>
      ) : adminId && !inProgress ? (
        <>
          <Box className={classes.userCard} style={{ marginBottom: '2rem' }}>
            <Box className={classes.userCardLeft}>
              <Avatar className={classes.avatar}>
                {admin.fullName?.charAt(0)?.toUpperCase() ?? 'U'}
              </Avatar>
              <Box className={classes.identity}>
                <Typography className={classes.fullName}>{admin.fullName}</Typography>
                <Typography className={classes.emailText}>{admin.email}</Typography>
                {(() => {
                  const role = admin.conferenceRoles?.[confId] ?? admin.role ?? 'admin'
                  const isOwner = role === 'owner'
                  return (
                    <Chip
                      label={role.charAt(0).toUpperCase() + role.slice(1)}
                      size="small"
                      className={isOwner ? classes.ownerRoleChip : classes.adminRoleChip}
                    />
                  )
                })()}
              </Box>
            </Box>

            <Divider orientation="vertical" flexItem className={classes.dividerVertical} />

            <Box className={classes.conferencesSection}>
              <Typography className={classes.confSectionLabel}>
                Linked Conferences (
                {
                  (admin.conferenceIds || (admin.conferenceId ? [admin.conferenceId] : [])).filter(
                    Boolean
                  ).length
                }
                )
              </Typography>
              <Box className={classes.conferenceList}>
                {(admin.conferenceIds || (admin.conferenceId ? [admin.conferenceId] : []))
                  .filter(Boolean)
                  .map((cId) => {
                    const confName = getConferenceName(cId)
                    const isCurrent = cId === confId
                    const isExpired = isConferenceExpired(cId)
                    return (
                      <Box
                        key={cId}
                        className={`${classes.conferenceRow} ${
                          isCurrent ? classes.currentConf : ''
                        }`}>
                        <Box
                          className={classes.confDot}
                          style={{ backgroundColor: isCurrent ? '#0ea5e9' : '#94a3b8' }}
                        />
                        <Typography
                          className={classes.confRowName}
                          style={isExpired ? { color: '#94a3b8' } : {}}>
                          {confName ?? cId}
                        </Typography>
                        {isExpired && (
                          <Chip label="Expired" size="small" className={classes.expiredChip} />
                        )}
                      </Box>
                    )
                  })}
              </Box>
            </Box>
          </Box>

          <Formik
            initialValues={{
              fullName: admin?.fullName ?? '',
              newPassword: '',
              role: admin?.conferenceRoles?.[confId] ?? admin?.role ?? 'admin'
            }}
            validationSchema={updateUserSchema}
            onSubmit={async (values) => {
              if (values.newPassword) {
                setPendingPasswordUpdate(values)
                return
              }
              await submitAdminUpdate(values)
            }}>
            {({ values, setFieldValue }) => (
              <Form autoComplete="off" style={{ marginBottom: '1rem', marginTop: '0.5rem' }}>
                <Grid container spacing={3} alignItems="flex-start" justifyContent="space-between">
                  <Grid item xs={12} md={4}>
                    <CustomTextField
                      label="Full Name"
                      name="fullName"
                      type="text"
                      autoComplete="off"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth variant="outlined">
                      <InputLabel>Role</InputLabel>
                      <Select
                        value={values.role}
                        onChange={(e) => setFieldValue('role', e.target.value)}
                        label="Role">
                        {getAvailableRoles().map((role) => (
                          <MenuItem key={role.value} value={role.value}>
                            {role.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <CustomTextField
                      label="New Password"
                      name="newPassword"
                      type="text"
                      autoComplete="off"
                      disabled={admin.mustChangePassword}
                    />
                    {admin.mustChangePassword && (
                      <Typography className={classes.pendingActivationNote}>
                        This user hasn&apos;t completed account setup yet — password can&apos;t be
                        changed until then. Use &quot;Resend Activation Email&quot; from the list
                        instead.
                      </Typography>
                    )}
                  </Grid>
                </Grid>

                <Box className={classes.buttonBox}>
                  <Button size="large" disabled={loading} onClick={() => history.goBack()}>
                    Cancel
                  </Button>
                  <ProgressButton
                    type="submit"
                    label="Update"
                    inProgress={loading}
                    disabled={loading}
                  />
                </Box>
              </Form>
            )}
          </Formik>
        </>
      ) : (
        <Formik
          initialValues={initialValues}
          validationSchema={adminsFormSchema}
          onSubmit={submitHandler}>
          {({ values, setFieldValue }) => (
            <Form autoComplete="off">
              <Alert severity="info" className={classes.guideNote}>
                This user will be invited by email with the temporary password below and a link to
                set their own password. They must complete that setup before they can log in — the
                link expires after 48 hours.
              </Alert>

              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <CustomTextField
                    label="Full Name"
                    name="fullName"
                    type="text"
                    autoComplete="off"
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <CustomTextField label="Email" name="email" autoComplete="off" />
                </Grid>

                <Grid item xs={12} md={4}>
                  <CustomTextField
                    label="Temporary Password"
                    name="password"
                    type="text"
                    autoComplete="off"
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>Role</InputLabel>
                    <Select
                      value={values.role}
                      onChange={(e) => setFieldValue('role', e.target.value)}
                      label="Role">
                      {getAvailableRoles().map((role) => (
                        <MenuItem key={role.value} value={role.value}>
                          {role.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: '0.75rem',
                  mt: '1rem',
                  justifyContent: 'flex-end'
                }}>
                <Grid item>
                  <Button size="large" disabled={loading} onClick={() => history.goBack()}>
                    Cancel
                  </Button>
                </Grid>
                <Grid item>
                  <ProgressButton
                    type="submit"
                    label="Create"
                    inProgress={loading}
                    disabled={loading}
                  />
                </Grid>
              </Box>
            </Form>
          )}
        </Formik>
      )}

      <DuplicateAssignmentDialog
        conferences={duplicateConferences}
        inProgress={loading}
        onCancel={handleCancelDuplicate}
        onConfirm={handleConfirmDuplicate}
      />

      <ConfirmModal
        dialogTitle="Change Password"
        dialogText={`Are you sure you want to change the password for ${
          admin?.fullName || 'this user'
        }? They will need to use the new password the next time they log in.`}
        open={Boolean(pendingPasswordUpdate)}
        inProgress={loading}
        closeModal={() => setPendingPasswordUpdate(null)}
        handleConfirm={handleConfirmPasswordUpdate}
      />
    </CustomBox>
  )
}
export default AdminsForm

const useStyles = makeStyles({
  confBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    padding: '0.75rem 1.25rem',
    borderRadius: '8px',
    backgroundColor: '#f0f9ff',
    border: '1px solid #bae6fd'
  },
  confBannerLabel: {
    fontSize: '0.75rem',
    fontWeight: 700,
    color: '#0369a1',
    textTransform: 'uppercase',
    letterSpacing: '0.05em'
  },
  confBannerName: {
    fontSize: '0.9rem',
    fontWeight: 600,
    color: '#0f172a'
  },
  buttonBox: {
    marginTop: '1rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    flexDirection: 'row',
    gap: '0.75rem'
  },
  userCard: {
    display: 'flex',
    gap: '2rem',
    padding: '1.5rem',
    borderRadius: '12px',
    border: '1px solid #e2e8f0',
    backgroundColor: '#ffffff',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    flexWrap: 'wrap',
    alignItems: 'flex-start'
  },
  userCardLeft: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '1rem',
    flex: '0 0 auto'
  },
  avatar: {
    width: 56,
    height: 56,
    fontSize: '1.4rem',
    fontWeight: 700,
    backgroundColor: '#0ea5e9',
    color: '#fff'
  },
  identity: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.3rem'
  },
  fullName: {
    fontSize: '1.1rem',
    fontWeight: 700,
    color: '#0f172a'
  },
  emailText: {
    fontSize: '0.875rem',
    color: '#64748b'
  },
  ownerRoleChip: {
    backgroundColor: '#dbeafe',
    color: '#1d4ed8',
    fontWeight: 600,
    fontSize: 11,
    height: 22,
    alignSelf: 'flex-start',
    marginTop: '0.2rem'
  },
  adminRoleChip: {
    backgroundColor: '#f1f5f9',
    color: '#475569',
    fontWeight: 600,
    fontSize: 11,
    height: 22,
    alignSelf: 'flex-start',
    marginTop: '0.2rem'
  },
  dividerVertical: {
    backgroundColor: '#e2e8f0',
    alignSelf: 'stretch'
  },
  conferencesSection: {
    flex: 1,
    minWidth: 220
  },
  confSectionLabel: {
    fontSize: '0.75rem',
    fontWeight: 700,
    color: '#94a3b8',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: '0.75rem'
  },
  conferenceList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem'
  },
  conferenceRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.6rem',
    padding: '0.5rem 0.75rem',
    borderRadius: '8px',
    backgroundColor: '#f8fafc',
    border: '1px solid #e2e8f0'
  },
  currentConf: {
    backgroundColor: '#f0f9ff',
    borderColor: '#bae6fd'
  },
  confDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    flexShrink: 0
  },
  confRowName: {
    fontSize: '0.875rem',
    fontWeight: 500,
    color: '#334155',
    flex: 1
  },
  currentChip: {
    backgroundColor: '#e0f2fe',
    color: '#0369a1',
    fontWeight: 600,
    fontSize: 10,
    height: 20
  },
  ownerChip: {
    backgroundColor: '#dbeafe',
    color: '#1d4ed8',
    fontWeight: 600,
    fontSize: 11,
    height: 22
  },
  adminChip: {
    backgroundColor: '#f1f5f9',
    color: '#475569',
    fontWeight: 600,
    fontSize: 11,
    height: 22
  },
  expiredChip: {
    backgroundColor: '#fef2f2',
    color: '#ef4444',
    fontWeight: 700,
    fontSize: 11,
    height: 22
  },
  pendingActivationNote: {
    fontSize: '0.75rem',
    color: '#b45309',
    marginTop: '-4px'
  },
  guideNote: {
    marginBottom: '1.5rem'
  }
})
